import { useTranslation } from 'react-i18next'
import { ErrorState, errorMessage } from '@/components/common/States'
import { useAttachments, useDownloadAttachment } from '@/hooks/useAttachments'
import { dateTimeLabel, fileBadge, formatBytes } from '@/lib/threadView'

/** Attachments tab: every file the caller may see, with presigned download. */
export function AttachmentsTab({ routeId, timeZone }: { routeId: string; timeZone: string }) {
  const { t } = useTranslation()
  const files = useAttachments(routeId, true)
  const download = useDownloadAttachment(routeId)

  if (files.isPending) return <p className="text-meta text-ink-muted">{t('common.loading')}</p>
  if (files.isError) return <ErrorState message={errorMessage(files.error)} onRetry={() => void files.refetch()} />
  if (files.data.length === 0) return <p className="text-meta text-ink-muted">{t('attachmentsTab.empty')}</p>

  return (
    <ul className="flex flex-col">
      {files.data.map((f) => (
        <li key={f.id} className="flex items-center gap-3 border-b border-line py-3">
          <span className="w-10 shrink-0 font-mono text-label text-ink-muted">{fileBadge(f.fileName, f.mimeType)}</span>
          <button
            type="button"
            onClick={() => download.mutate(f.id)}
            className="min-w-0 grow truncate text-left text-body text-ink-strong hover:underline"
            title={t('ticket.message.download', { name: f.fileName })}
          >
            {f.fileName}
          </button>
          <span className="w-16 shrink-0 text-right text-label text-ink-faint">{formatBytes(f.sizeBytes)}</span>
          <span className="w-20 shrink-0 text-label">
            {f.visibility === 'internal' ? (
              <span className="rounded-sm bg-note-bg px-1.5 py-0.5 text-note-heading">{t('attachmentsTab.internal')}</span>
            ) : (
              <span className="text-ink-muted">{t('attachmentsTab.customer')}</span>
            )}
          </span>
          <span className="w-[170px] shrink-0 text-right font-mono text-label text-ink-muted">{dateTimeLabel(f.uploadedAt, timeZone)}</span>
        </li>
      ))}
      {download.error ? (
        <li role="alert" className="pt-2 text-meta text-status-escalated-fg">
          {errorMessage(download.error)}
        </li>
      ) : null}
    </ul>
  )
}
